import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.ts';
import type { User } from './types.ts';

/** The signed-in person's own account, and the system's branding. */

export interface Branding {
  hasLogo: boolean;
  updatedAt: string | null;
}

export function useBranding() {
  return useQuery({
    queryKey: ['branding'],
    queryFn: () => api.get<Branding>('/api/branding'),
    // Rarely changes, and it is rendered on every page.
    staleTime: 5 * 60_000,
  });
}

/**
 * The logo's URL, or null to fall back to the shipped mark.
 *
 * Stamped with the upload time so replacing the logo takes effect at once
 * instead of after the cache expires.
 */
export function logoUrl(branding: Branding | undefined): string | null {
  if (!branding?.hasLogo) return null;
  return `/api/branding/logo?v=${encodeURIComponent(branding.updatedAt ?? '')}`;
}

export function useUpdateProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name?: string;
      timeZone?: string;
      density?: 'comfortable' | 'compact';
      completeSetup?: boolean;
    }) => api.post<User>('/api/profile', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useUploadAvatar() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload<{ avatarUpdatedAt: string }>('/api/profile/avatar', form);
    },
  });
}

export function useClearAvatar() {
  return useMutation({ mutationFn: () => api.delete('/api/profile/avatar') });
}

export function useUploadLogo() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload<{ updatedAt: string }>('/api/branding/logo', form);
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['branding'] }),
  });
}

export function useClearLogo() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete('/api/branding/logo'),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['branding'] }),
  });
}

// --- Sessions ---------------------------------------------------------------

export interface ActiveSession {
  id: string;
  ipAddress: string | null;
  userAgent: string | null;
  deviceLabel: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
  isCurrent: boolean;
}

export function useSessions() {
  return useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<ActiveSession[]>('/api/auth/sessions'),
  });
}

/**
 * End one device's session.
 *
 * Returns whether that device was this one, which the caller needs: signing
 * out the browser you are holding has to finish by clearing the local
 * session, not by refetching a list you can no longer read.
 */
export function useRevokeSession() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.delete<{ wasCurrent: boolean }>(`/api/auth/sessions/${sessionId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['sessions'] }),
  });
}

export function useRevokeOtherSessions() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/auth/sessions/revoke-all'),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['sessions'] }),
  });
}

// --- Two-factor -------------------------------------------------------------

export interface TotpEnrolment {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl?: string;
}

export function useBeginTotp() {
  return useMutation({
    mutationFn: () => api.post<TotpEnrolment>('/api/auth/totp/enrol'),
  });
}

export function useConfirmTotp() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api.post<{ backupCodes: string[] }>('/api/auth/totp/confirm', { code }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['me'] });
      void client.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post('/api/auth/password', input),
  });
}

// --- Audit ------------------------------------------------------------------

export interface AuditEvent {
  id: string;
  sequence: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  changes: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  actor: { id: string; name: string; email: string; hasAvatar: boolean } | null;
}

export interface AuditPage {
  events: AuditEvent[];
  nextCursor: string | null;
}

export function useAuditLog(filter: { action?: string; q?: string; before?: string }) {
  const params = new URLSearchParams();
  if (filter.action) params.set('action', filter.action);
  if (filter.q) params.set('q', filter.q);
  if (filter.before) params.set('before', filter.before);

  return useQuery({
    queryKey: ['audit', filter.action ?? '', filter.q ?? '', filter.before ?? ''],
    queryFn: () => api.get<AuditPage>(`/api/audit?${params.toString()}`),
  });
}

export function useAuditActions() {
  return useQuery({
    queryKey: ['audit-actions'],
    queryFn: () => api.get<{ action: string; count: number }[]>('/api/audit/actions'),
    staleTime: 60_000,
  });
}

export function useAuditVerification() {
  return useQuery({
    queryKey: ['audit-verify'],
    queryFn: () =>
      api.get<
        { intact: true; checked: number } | { intact: false; brokenAt: string; reason: string }
      >('/api/audit/verify'),
    staleTime: 60_000,
  });
}
